import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import SelectTree from '@app/components/select-tree/SelectTree';
import { NIL as NIL_UUID } from 'uuid';

import { messageError, messageToast } from '../../utils/define';
import { editYeuCauAction, yeuCauGetItemAction, yeuCauGetFilesAction, yeuCauDeleteFileAction } from '../../store/reducers/yeucau';
import { linhVucGetAllAction } from '../../store/reducers/linhvuc';
import { loaiYeuCauGetAllAction } from '../../store/reducers/loaiyeucau';
import { khoaGetAllAction } from '../../store/reducers/khoa';
import { TRANG_THAI_YEU_CAU_OPTIONS, MUC_DO_UU_TIEN } from '../../store/constants';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';
import { getNguoiDungByAccountId, getNguoiDungById } from '../../store/reducers/nguoidung';
import { Get, PostFormData, PutFormData } from '../../utils/httpProvider';
import { NGUOIPHOIHOP_API, NHANVIEN_API, YEUCAU_API, BaseGatewayImage } from '../../utils/apiUrl';
import { MUC_DO_PHUC_TAP_OPTIONS } from '../../store/constants';

const schema = yup.object().shape({
    nguoiDung: yup.string().required('Vui lòng nhập Người yêu cầu'),
    linhVucId: yup.number().required('Vui lòng chọn lĩnh vực'),
    loaiYeuCauId: yup.mixed().required('Vui lòng chọn loại yêu cầu'),
    khoaIds: yup.array().min(1, 'Vui lòng chọn ít nhất 1 khoa'),
    ngayBatDau: yup.string().nullable(),
    linkTicket: yup.string().url('Link ticket không hợp lệ'),
    noiDung: yup.string().nullable(),
    mucDo: yup.string().required('Vui lòng chọn mức độ'),
    nguoiPhoiHopIds: yup.array().nullable(),
    ngayHoanThanhDuKien: yup.string().nullable(),
    ngayHoanThanhThucTe: yup.string().nullable(),
    tienDo: yup.number().min(0).max(100).required('Vui lòng nhập tiến độ'),
    // status can be numeric or string depending on backend/options; accept either non-empty string or number
    status: yup.mixed().test('required-status', 'Vui lòng chọn trạng thái', value => {
        if (value === undefined || value === null) return false;
        if (typeof value === 'number') return !isNaN(value);
        if (typeof value === 'string') return value.trim().length > 0;
        return true;
    }),
    ghiChu: yup.string(),
    nhanVienThucHienIds: yup.array(),
});

const YeuCauEdit = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const yeuCauItem = useSelector((state) => state.yeuCau?.yeuCauItem);
    const listLinhVuc = useSelector((state) => state.linhvuc.listLinhVuc || []);
    const listLoaiYeuCau = useSelector((state) => state.loaiyeucau.listLoaiYeuCau || []);
    const listKhoa = useSelector((state) => state.khoa.listKhoa || []);

    const [isLoading, setIsLoading] = useState(false);
    const [mappedNguoiDungId, setMappedNguoiDungId] = useState(null);
    const [mappedNguoiDungName, setMappedNguoiDungName] = useState(null);
    const [existingFiles, setExistingFiles] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [listNguoiPhoiHop, setListNguoiPhoiHop] = useState([]);
    const [listNhanVien, setListNhanVien] = useState([]);

    const fileInputRef = useRef(null);

    // Lấy id từ URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get('id');
        if (id) dispatch(yeuCauGetItemAction(id));
        if (id) dispatch(yeuCauGetFilesAction(id));
    }, [dispatch, location.search]);

    // Load Lĩnh vực và Khoa
    useEffect(() => {
        if (!listLinhVuc.length) dispatch(linhVucGetAllAction());
        if (!listLoaiYeuCau.length) dispatch(loaiYeuCauGetAllAction());
        if (!listKhoa.length) dispatch(khoaGetAllAction());
        (async () => {
            try {
                const res = await Get(NGUOIPHOIHOP_API.GET_ALL, null, null);
                let items = [];
                if (Array.isArray(res)) items = res;
                else if (res && res.data && Array.isArray(res.data)) items = res.data;
                const mapped = (items || []).map(u => ({ id: String(u.id || u.nguoiPhoiHopId || u.id), name: u.name || u.hoTen || u.ten || u.displayName || String(u.id), parentId: u.parentId || NIL_UUID }));
                setListNguoiPhoiHop(mapped);
            } catch (e) { setListNguoiPhoiHop([]); }

            try {
                const res2 = await Get(NHANVIEN_API.BASE, null, null);
                let items2 = [];
                if (Array.isArray(res2)) items2 = res2;
                else if (res2 && res2.data && Array.isArray(res2.data)) items2 = res2.data;
                const mapped2 = (items2 || []).map(u => ({ id: String(u.id || u.nhanVienId), name: u.hoTen || u.fullName || u.name || u.ten || String(u.id), parentId: u.khoaId || NIL_UUID }));
                setListNhanVien(mapped2);
            } catch (e) { setListNhanVien([]); }
        })();
    }, [dispatch, listLinhVuc.length, listLoaiYeuCau.length, listKhoa.length]);

    // fetch display name for nguoiDungId present in yeuCauItem
    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!yeuCauItem?.nguoiDungId) return;
            const id = yeuCauItem.nguoiDungId;
            setMappedNguoiDungId(id);
            try {
                const res = await dispatch(getNguoiDungById(id)).unwrap();
                let item = res || null;
                if (Array.isArray(item)) item = item[0] || null;
                if (item && item.data) item = item.data;
                if (Array.isArray(item)) item = item[0] || null;
                const name = item?.hoTen || item?.fullName || item?.name || item?.ten || item?.displayName || null;
                if (mounted && name) {
                    setMappedNguoiDungName(name);
                    return;
                }
            } catch (e) { }
            try {
                const res2 = await dispatch(getNguoiDungByAccountId(id)).unwrap();
                let item2 = res2 || null;
                if (Array.isArray(item2)) item2 = item2[0] || null;
                if (item2 && item2.data) item2 = item2.data;
                if (Array.isArray(item2)) item2 = item2[0] || null;
                const name2 = item2?.hoTen || item2?.fullName || item2?.name || item2?.ten || item2?.displayName || null;
                if (mounted && name2) setMappedNguoiDungName(name2);
            } catch (e) { }
        })();
        return () => { mounted = false; };
    }, [dispatch, yeuCauItem]);

    // watch files state from store
    const filesByYeuCau = useSelector(state => state.yeuCau.filesByYeuCau || {});
    useEffect(() => {
        if (yeuCauItem?.id && filesByYeuCau[yeuCauItem.id]) {
            setExistingFiles(filesByYeuCau[yeuCauItem.id]);
        }
    }, [filesByYeuCau, yeuCauItem]);

    const normalizeDateForInput = (dateStr) => {
        if (!dateStr) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        if (typeof dateStr === 'string' && dateStr.includes('T')) return dateStr.split('T')[0];
        const dt = new Date(dateStr);
        if (isNaN(dt.getTime())) return '';
        const yyyy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const resolveStatusValue = (rawStatus) => {
        if (rawStatus === undefined || rawStatus === null) return 0;
        // find by numeric value
        const byValue = TRANG_THAI_YEU_CAU_OPTIONS.find(o => o.value === rawStatus || o.value === Number(rawStatus));
        if (byValue) return byValue.value;
        // find by key (string like 'chophanhoi')
        const byKey = TRANG_THAI_YEU_CAU_OPTIONS.find(o => o.key === rawStatus);
        if (byKey) return byKey.value;
        return rawStatus;
    };

    const initialNguoiDungDisplay = mappedNguoiDungName || yeuCauItem?.nguoiDungId || '';
    const initialValues = {
        nguoiDung: initialNguoiDungDisplay,
        linhVucId: yeuCauItem?.linhVucId || '',
        loaiYeuCauId: yeuCauItem?.loaiYeuCauId || '',
        khoaIds: Array.isArray(yeuCauItem?.khoaIds) ? yeuCauItem.khoaIds : [],
        ngayBatDau: normalizeDateForInput(yeuCauItem?.ngayBatDau),
        linkTicket: yeuCauItem?.linkTicket || '',
        noiDung: yeuCauItem?.noiDung || '',
        mucDo: yeuCauItem?.mucDo || '',
        mucDoPhucTap: yeuCauItem?.mucDoPhucTap || '',
        nguoiPhoiHopIds: Array.isArray(yeuCauItem?.nguoiPhoiHopIds) ? yeuCauItem.nguoiPhoiHopIds.map(id => String(id)) : (Array.isArray(yeuCauItem?.nguoiPhoiHop) ? yeuCauItem.nguoiPhoiHop.map(id => String(id)) : (yeuCauItem?.nguoiPhoiHop ? [String(yeuCauItem.nguoiPhoiHop)] : [])),
        ngayHoanThanhDuKien: normalizeDateForInput(yeuCauItem?.ngayHoanThanhDuKien),
        ngayHoanThanhThucTe: normalizeDateForInput(yeuCauItem?.ngayHoanThanhThucTe),
        tienDo: yeuCauItem?.tienDo || 0,
        status: resolveStatusValue(yeuCauItem?.status),
        ghiChu: yeuCauItem?.ghiChu || '',
        nhanVienThucHienIds: Array.isArray(yeuCauItem?.nhanVienThucHienIds) ? yeuCauItem.nhanVienThucHienIds.map(id => String(id)) : (Array.isArray(yeuCauItem?.nguoiThucHien) ? yeuCauItem.nguoiThucHien.map(id => String(id)) : (yeuCauItem?.nguoiThucHien ? [String(yeuCauItem.nguoiThucHien)] : [])),
    };

    const toFileKey = (f) => `${f.name}__${f.size}__${f.lastModified}`;
    const formatBytes = (bytes) => {
        if (!bytes && bytes !== 0) return '';
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        let v = bytes;
        let i = 0;
        while (v >= 1024 && i < units.length - 1) {
            v = v / 1024;
            i += 1;
        }
        return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
    };

    // Helper: convert camelCase keys to PascalCase recursively (keeps arrays intact)
    const toPascalKeys = (input) => {
        if (input === null || input === undefined) return input;
        if (Array.isArray(input)) {
            return input.map((v) => (v && typeof v === 'object' && !(v instanceof Date) ? toPascalKeys(v) : v));
        }
        if (typeof input === 'object') {
            const out = {};
            Object.keys(input).forEach((k) => {
                const pascalKey = k && k.length > 0 ? `${k.charAt(0).toUpperCase()}${k.slice(1)}` : k;
                const val = input[k];
                if (val === null || val === undefined) {
                    out[pascalKey] = val;
                } else if (Array.isArray(val)) {
                    out[pascalKey] = val.map((v) => (v && typeof v === 'object' && !(v instanceof Date) ? toPascalKeys(v) : v));
                } else if (typeof val === 'object' && !(val instanceof Date) && !(val instanceof File)) {
                    out[pascalKey] = toPascalKeys(val);
                } else {
                    out[pascalKey] = val;
                }
            });
            return out;
        }
        return input;
    };

    // Helper: ensure backend datetime format (append time if only date provided)
    const normalizeDateForApi = (dateStr) => {
        if (!dateStr) return null;
        if (typeof dateStr !== 'string') return dateStr;
        if (dateStr.includes('T')) return dateStr;
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return `${dateStr}T00:00:00`;
        return dateStr;
    };

    const handleEdit = async (values) => {
        if (!yeuCauItem?.id) {
            toast.error('Không tìm thấy Yêu Cầu để cập nhật!');
            return;
        }

        const nguoiDungIdToSend = mappedNguoiDungId || values.nguoiDung || '00000000-0000-0000-0000-000000000000';
        const modelUpdate = {
            id: yeuCauItem.id,
            data: {
                ...values,
                nguoiDungId: nguoiDungIdToSend,
            },
        };

        try {
            setIsLoading(true);

            // Always send multipart/form-data for update (backend expects multipart even without new files)
            const formData = new FormData();

            const nguoiDungId = mappedNguoiDungId || values.nguoiDung || '';
            if (nguoiDungId) formData.append('NguoiDungId', String(nguoiDungId));

            if (values.linhVucId !== undefined && values.linhVucId !== null) formData.append('LinhVucId', String(values.linhVucId));
            if (values.loaiYeuCauId !== undefined && values.loaiYeuCauId !== null && values.loaiYeuCauId !== '') {
                formData.append('LoaiYeuCauId', String(values.loaiYeuCauId));
            }
            if (Array.isArray(values.khoaIds)) values.khoaIds.forEach((k, idx) => formData.append(`KhoaIds[${idx}]`, String(k)));

            // Dates: keep as ISO date/time if available
            if (values.ngayBatDau) formData.append('NgayBatDau', String(values.ngayBatDau));
            if (values.linkTicket !== undefined) formData.append('LinkTicket', String(values.linkTicket || ''));
            if (values.noiDung !== undefined) formData.append('NoiDung', String(values.noiDung || ''));
            if (values.mucDo !== undefined) formData.append('MucDo', String(values.mucDo || ''));
            if (values.mucDoPhucTap !== undefined) formData.append('MucDoPhucTap', String(values.mucDoPhucTap || ''));
            if (values.nguoiPhoiHop !== undefined) formData.append('NguoiPhoiHop', String(values.nguoiPhoiHop || ''));
            if (Array.isArray(values.nguoiPhoiHopIds)) values.nguoiPhoiHopIds.forEach((id, idx) => formData.append(`NguoiPhoiHopIds[${idx}]`, String(id)));
            if (Array.isArray(values.nhanVienThucHienIds)) values.nhanVienThucHienIds.forEach((id, idx) => formData.append(`NhanVienThucHienIds[${idx}]`, String(id)));

            if (values.ngayHoanThanhDuKien !== undefined) formData.append('NgayHoanThanhDuKien', String(values.ngayHoanThanhDuKien || ''));
            if (values.ngayHoanThanhThucTe !== undefined) formData.append('NgayHoanThanhThucTe', String(values.ngayHoanThanhThucTe || ''));
            if (values.tienDo !== undefined && values.tienDo !== null) formData.append('TienDo', String(values.tienDo));
            if (values.status !== undefined && values.status !== null) formData.append('Status', String(values.status));
            if (values.ghiChu !== undefined && values.ghiChu !== null) formData.append('GhiChu', String(values.ghiChu || ''));

            // Include existing file identifiers so backend can keep them (use key expected by backend)
            if (Array.isArray(existingFiles) && existingFiles.length > 0) {
                existingFiles.forEach((f, idx) => {
                    const fid = f.id || f.fileId || f.FileId || f.fileId || null;
                    if (fid) formData.append(`ExistingFileIds[${idx}]`, String(fid));
                    // also include original file objects in case backend expects 'files' array entries
                    // we add a minimal JSON descriptor under key FilesJson (optional)
                });
            }

            // Attach new files (if any)
            if (Array.isArray(newFiles) && newFiles.length > 0) {
                newFiles.forEach((f) => { if (f) formData.append('Files', f); });
            }

            // Always use PUT multipart/form-data for update to avoid 415
            await PutFormData(YEUCAU_API.UPDATE(yeuCauItem.id), null, formData);

            // refresh files and finish
            dispatch(yeuCauGetFilesAction(yeuCauItem.id));
            toast.success(messageToast.messageToastUpdateSuccess);
            history.goBack();
        } catch (err) {
            console.error('Update failed', err);
            toast.error('Cập nhật dữ liệu không thành công');
        } finally {
            setIsLoading(false);
        }

    };

    const handleNewFilesChange = (e) => {
        const picked = Array.from(e.target.files || []);
        if (picked.length === 0) return;

        setNewFiles((prev) => {
            const map = new Map((prev || []).map((f) => [toFileKey(f), f]));
            picked.forEach((f) => map.set(toFileKey(f), f));
            return Array.from(map.values());
        });

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemoveFile = (index) => {
        setNewFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleClearFiles = () => {
        setNewFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm('Bạn có chắc muốn xóa tệp này?')) return;
        try {
            await dispatch(yeuCauDeleteFileAction(fileId)).unwrap();
            // refresh file list
            if (yeuCauItem?.id) dispatch(yeuCauGetFilesAction(yeuCauItem.id));
        } catch (e) {
            toast.error('Xóa tệp thất bại');
        }
    };

    if (!yeuCauItem) return <ReactSpinner loadings={true} />;

    return (
        <Card>
            <Card.Header as="h5">Sửa Yêu Cầu</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={handleEdit}
                    initialValues={initialValues}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            {/* Người yêu cầu */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Người tạo yêu cầu</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        disabled
                                        readOnly
                                        type="text"
                                        name="nguoiDung"
                                        value={values.nguoiDung}
                                        onChange={(e) => {
                                            setFieldValue('nguoiDung', e.target.value);
                                            setMappedNguoiDungId(null);
                                            setMappedNguoiDungName(e.target.value);
                                        }}
                                        isInvalid={!!errors.nguoiDung}
                                        placeholder="Nhập ID hoặc tên người yêu cầu"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.nguoiDung}</Form.Control.Feedback>
                                    <input type="hidden" name="nguoiDungId" value={mappedNguoiDungId || yeuCauItem?.nguoiDungId || ''} />
                                </Col>
                            </Form.Group>

                            {/* Lĩnh vực */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Lĩnh vực</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="select"
                                        name="linhVucId"
                                        value={values.linhVucId || ''}
                                        onChange={(e) => setFieldValue('linhVucId', Number(e.target.value))}
                                        isInvalid={!!errors.linhVucId}
                                    >
                                        <option value="">-- Chọn lĩnh vực --</option>
                                        {listLinhVuc.map((lv) => (<option key={lv.id} value={lv.id}>{lv.name}</option>))}
                                    </Form.Control>
                                    {errors.linhVucId && <div className="text-danger mt-1">{errors.linhVucId}</div>}
                                </Col>
                            </Form.Group>

                            {/* Loại yêu cầu */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Loại yêu cầu</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="select"
                                        name="loaiYeuCauId"
                                        value={values.loaiYeuCauId || ''}
                                        onChange={(e) => setFieldValue('loaiYeuCauId', e.target.value ? Number(e.target.value) : '')}
                                        isInvalid={!!errors.loaiYeuCauId}
                                    >
                                        <option value="">-- Chọn loại yêu cầu --</option>
                                        {listLoaiYeuCau.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {item.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.loaiYeuCauId}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Khoa */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Khoa yêu cầu
                                    {values.khoaIds?.length > 0 && (
                                        <span className="text-primary ms-1">
                                            ({values.khoaIds.length})
                                        </span>
                                    )}</Form.Label>
                                <Col sm="10">
                                    <SelectTree
                                        data={listKhoa.map((k) => ({ id: k.id, name: k.name, parentId: k.parentId || NIL_UUID }))}
                                        showSearchBox
                                        Placeholder="Chọn khoa hỗ trợ"
                                        positionExpand="right"
                                        multiSelect
                                        multiValueSelected={values.khoaIds}
                                        onChange={(arr) => setFieldValue('khoaIds', arr.map(x => x.id))}
                                    />
                                    {errors.khoaIds && <div className="invalid-feedback d-block">{errors.khoaIds}</div>}
                                </Col>
                            </Form.Group>

                            {/* Thời gian */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label column sm={2}>
                                    Thời gian
                                </Form.Label>

                                <Col sm={10}>
                                    <Row className="g-3">
                                        {/* Ngày bắt đầu */}
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="text-muted small mb-1">
                                                    Ngày bắt đầu
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="ngayBatDau"
                                                    value={values.ngayBatDau}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.ngayBatDau}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.ngayBatDau}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        {/* Ngày dự kiến hoàn thành */}
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="text-muted small mb-1">
                                                    Ngày dự kiến hoàn thành
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="ngayHoanThanhDuKien"
                                                    value={values.ngayHoanThanhDuKien}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.ngayHoanThanhDuKien}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.ngayHoanThanhDuKien}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        {/* Ngày hoàn thành thực tế */}
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="text-muted small mb-1">
                                                    Ngày hoàn thành thực tế
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="ngayHoanThanhThucTe"
                                                    value={values.ngayHoanThanhThucTe}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.ngayHoanThanhThucTe}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.ngayHoanThanhThucTe}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Col>
                            </Form.Group>


                            {/* Link Ticket */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Link Ticket</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        type="text"
                                        name="linkTicket"
                                        value={values.linkTicket}
                                        onChange={handleChange}
                                        isInvalid={!!errors.linkTicket}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.linkTicket}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Nội dung */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Nội dung</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="textarea"
                                        rows={8}
                                        name="noiDung"
                                        value={values.noiDung}
                                        onChange={handleChange}
                                        isInvalid={!!errors.noiDung}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.noiDung}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Mức độ */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Mức độ</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="select"
                                        name="mucDo"
                                        value={values.mucDo}
                                        onChange={(e) => setFieldValue('mucDo', e.target.value)}
                                        isInvalid={!!errors.mucDo}
                                    >
                                        <option value="">-- Chọn mức độ --</option>
                                        {MUC_DO_UU_TIEN.map((opt) => (
                                            <option key={opt.key} value={opt.key}>{opt.label}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.mucDo}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Mức độ phức tạp */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Mức độ phức tạp</Form.Label>
                                <Col sm="10">
                                    <Form.Control as="select" name="mucDoPhucTap" value={values.mucDoPhucTap} onChange={(e) => setFieldValue('mucDoPhucTap', e.target.value)} isInvalid={!!errors.mucDoPhucTap}>
                                        <option value="">-- Chọn loại lỗi --</option>
                                        {MUC_DO_PHUC_TAP_OPTIONS.map(opt => (
                                            <option key={opt.key} value={opt.key}>{opt.label}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.mucDoPhucTap}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Người phối hợp (chọn nhiều giống Khoa) */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Người phối hợp</Form.Label>
                                <Col sm="10">
                                    <SelectTree
                                        data={listNguoiPhoiHop}
                                        showSearchBox
                                        Placeholder="Chọn người phối hợp"
                                        positionExpand="right"
                                        multiSelect
                                        multiValueSelected={values.nguoiPhoiHopIds || []}
                                        onChange={(arr) => setFieldValue('nguoiPhoiHopIds', arr.map(x => x.id))}
                                    />
                                    {errors.nguoiPhoiHopIds && <div className="invalid-feedback d-block">{errors.nguoiPhoiHopIds}</div>}
                                </Col>
                            </Form.Group>

                            {/* Người thực hiện (chọn nhiều nhân viên) */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Người thực hiện</Form.Label>
                                <Col sm="10">
                                    <SelectTree
                                        data={listNhanVien}
                                        showSearchBox
                                        Placeholder="Chọn người thực hiện"
                                        positionExpand="right"
                                        multiSelect
                                        multiValueSelected={values.nhanVienThucHienIds || []}
                                        onChange={(arr) => setFieldValue('nhanVienThucHienIds', arr.map(x => x.id))}
                                    />
                                    {errors.nhanVienThucHienIds && <div className="invalid-feedback d-block">{errors.nhanVienThucHienIds}</div>}
                                </Col>
                            </Form.Group>

                            {/* Tiến độ */}
                            {/*<Form.Group as={Row} className="mb-3">*/}
                            {/*    <Form.Label className="col-sm-2 col-form-label">Tiến độ (%)</Form.Label>*/}
                            {/*    <Col sm="10">*/}
                            {/*        <Form.Control*/}
                            {/*            type="number"*/}
                            {/*            name="tienDo"*/}
                            {/*            value={values.tienDo}*/}
                            {/*            onChange={handleChange}*/}
                            {/*            isInvalid={!!errors.tienDo}*/}
                            {/*            min={0} max={100}*/}
                            {/*        />*/}
                            {/*        <Form.Control.Feedback type="invalid">{errors.tienDo}</Form.Control.Feedback>*/}
                            {/*    </Col>*/}
                            {/*</Form.Group>*/}

                            {/* Trạng thái */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Trạng thái</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="select"
                                        name="status"
                                        value={values.status}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            const num = Number(raw);
                                            // convert numeric strings to numbers; keep string keys (e.g. 'chophanhoi') as-is
                                            setFieldValue('status', (raw !== '' && !isNaN(num)) ? num : raw);
                                        }}
                                        isInvalid={!!errors.status}
                                    >
                                        {TRANG_THAI_YEU_CAU_OPTIONS.map((opt) => (
                                            <option key={opt.key} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.status}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            {/* Ghi chú */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">Ghi chú</Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="ghiChu"
                                        value={values.ghiChu}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Form.Group>

                            {/* File đính kèm hiện tại */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    File đính kèm hiện tại
                                </Form.Label>

                                <Col sm="10">
                                    {existingFiles.length === 0 ? (
                                        <p className="mb-1">Chưa có tệp đính kèm.</p>
                                    ) : (
                                        <div className="border rounded">
                                            <div className="px-2 py-2">
                                                {existingFiles.map(f => {
                                                    const url =
                                                        f.fileUrl?.startsWith('http')
                                                            ? f.fileUrl
                                                            : `${BaseGatewayImage}${f.fileUrl || f.FileUrl || ''}`;

                                                    const display =
                                                        f.name || f.fileName || f.FileName || 'Tệp đính kèm';

                                                    return (
                                                        <div
                                                            key={f.id}
                                                            className="d-flex align-items-center justify-content-between py-2 border-bottom"
                                                        >
                                                            <a
                                                                href={url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-truncate me-2"
                                                            >
                                                                {display}
                                                            </a>

                                                            <Button
                                                                type="button"
                                                                theme="danger"
                                                                style={{ padding: '2px 10px', fontSize: 12 }}
                                                                onClick={() => handleDeleteFile(f.id)}
                                                            >
                                                                Xóa
                                                            </Button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </Col>
                            </Form.Group>


                            {/* Thêm tệp mới */}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Thêm tệp mới
                                </Form.Label>

                                <Col sm="10">
                                    <Form.Control
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleNewFilesChange}
                                    />

                                    {newFiles.length > 0 && (
                                        <div className="mt-2">
                                            <div className="border rounded">
                                                <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
                                                    <div className="text-muted">
                                                        Đã chọn {newFiles.length} file
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        theme="soft"
                                                        onClick={handleClearFiles}
                                                    >
                                                        Bỏ tất cả
                                                    </Button>
                                                </div>

                                                <div className="px-2 pb-1">
                                                    {newFiles.map((f, idx) => (
                                                        <div
                                                            key={toFileKey(f)}
                                                            className={`d-flex justify-content-between align-items-center py-2 ${idx < newFiles.length - 1
                                                                    ? 'border-bottom'
                                                                    : ''
                                                                }`}
                                                        >
                                                            <div className="me-3" style={{ minWidth: 0 }}>
                                                                <div className="fw-semibold text-truncate">
                                                                    {f.name}
                                                                </div>
                                                                <div className="text-muted small">
                                                                    {formatBytes(f.size)}
                                                                </div>
                                                            </div>

                                                            <Button
                                                                type="button"
                                                                theme="danger"
                                                                onClick={() => handleRemoveFile(idx)}
                                                                style={{
                                                                    padding: '2px 10px',
                                                                    fontSize: 12,
                                                                    lineHeight: '18px'
                                                                }}
                                                            >
                                                                Xóa
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </Col>
                            </Form.Group>


                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup>
                                        <Button type="submit" theme="primary" disabled={isLoading}>Sửa</Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button theme="secondary" onClick={() => history.goBack()}>Hủy</Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>

                <ReactSpinner loadings={isLoading} />
            </Card.Body>
        </Card>
    );
};

export default YeuCauEdit;
